import React, { useCallback, useState } from "react";

import { useDashboardContext } from "../../contexts/dashboard-context";

import { useDefinition } from "../../hooks/useDefinition";
import { useEditor } from "../../hooks/useEditor";
import { useLabels } from "../../hooks/useLabels";
import { useSnackbar } from "../../hooks/useSnackbar";

import { IconButton } from "../icon-button";

import { usePhotoSelection } from "../bottom-photo-menus/photo-selection-panel/use-photo-selection";
import { DialogButton, SimpleDialog } from "../dialog";
import { REMEMBER_AUTOFILL_TOOLTIP_SHOWN } from "./constants";
import { labels } from "./labels";
import "./autofill-button.css";

export function AutofillButton({ title, logGAEvent }: { title?: string; logGAEvent: () => void }) {
	const dashboard = useDashboardContext();
	const editor = useEditor();
	const definition = useDefinition();
	const photosInSelection = usePhotoSelection();
	const [
		[
			autofillNotificationLabel,
			partialAutofillNotificationLabel,
			noAutofillNotification,
			fillOrRefillDialogTitle,
			fillDialogMessage,
			fillOrRefillDialogMessage,
			spreadsAddedMessage,
			fillOrRefillDialogFill,
			fillOrRefillDialogRefill,
			fillOrRefillDialogCancel,
		],
		areLabelsLoading,
	] = useLabels([
		labels.autofillNotification,
		labels.partialAutofillNotification,
		labels.noAutofillNotification,
		labels.fillOrRefillDialogTitle,
		labels.fillDialogMessage,
		labels.fillOrRefillDialogMessage,
		labels.extraSpreadsMessage,
		labels.fillOrRefillDialogFill,
		labels.fillOrRefillDialogRefill,
		labels.fillOrRefillDialogCancel,
	]);

	const { addSnackbar } = useSnackbar();

	const [isDialogOpen, setIsDialogOpen] = useState(false);

	const getDialogMessage = useCallback(() => {
		const emptyPhotoZones = definition?.getEmptyPhotoZones(false);
		const unusedPhotosInSelection = photosInSelection.filter((photo) => photo.useCount === 0);
		const isEmptyProduct = unusedPhotosInSelection.length === photosInSelection.length;
		const canAddSpreads = eXpress.core.AutoFillManager.canAddSpreads(editor);
		const shouldAddPages =
			emptyPhotoZones && emptyPhotoZones.length < unusedPhotosInSelection.length;

		if (isEmptyProduct) {
			if (
				editor.definition?.product.classification === eXpress.core.Classification.Album &&
				shouldAddPages &&
				canAddSpreads
			) {
				return `${fillDialogMessage} ${spreadsAddedMessage}`;
			}
			return fillDialogMessage;
		}
		if (
			editor.definition?.product.classification === eXpress.core.Classification.Album &&
			shouldAddPages &&
			canAddSpreads
		) {
			return `${fillOrRefillDialogMessage} ${spreadsAddedMessage}`;
		}
		return fillOrRefillDialogMessage;
	}, [
		definition,
		editor,
		fillDialogMessage,
		fillOrRefillDialogMessage,
		photosInSelection,
		spreadsAddedMessage,
	]);

	const doAutofill = useCallback(
		async (fillChoice: eXpress.core.commands.AutoFillType) => {
			if (isDialogOpen) setIsDialogOpen(false);

			sessionStorage.setItem(REMEMBER_AUTOFILL_TOOLTIP_SHOWN, "true");
			const amountOfUsedPhotos = photosInSelection.filter((photo) => photo.isUsed).length;
			return dashboard
				.doAutoFillAsync(dashboard.getValidPhotosForAutoFill(photosInSelection), {
					clearMode:
						fillChoice === eXpress.core.commands.AutoFillType.Fill
							? eXpress.core.commands.ClearMode.None
							: eXpress.core.commands.ClearMode.Photos,
				})
				.then(() => {
					const newAmountOfUsedPhotos = photosInSelection.filter(
						(photo) => photo.isUsed,
					).length;
					const numberOfPhotoZones =
						editor.definition?.getFillablePhotoZones(false).length || 0;
					const numberOfEmptyPhotoZones =
						editor.definition?.getEmptyPhotoZones(false).length || 0;
					const numberOfFilledPhotoZones = numberOfPhotoZones - numberOfEmptyPhotoZones;

					let message: string | undefined;

					if (fillChoice === eXpress.core.commands.AutoFillType.Refill) {
						if (numberOfEmptyPhotoZones === 0) {
							message = autofillNotificationLabel;
						} else {
							message =
								partialAutofillNotificationLabel &&
								eXpress.utils.formatString(
									partialAutofillNotificationLabel,
									numberOfFilledPhotoZones.toString(),
									numberOfPhotoZones.toString(),
								);
						}
					} else if (fillChoice === eXpress.core.commands.AutoFillType.Fill) {
						if (amountOfUsedPhotos === newAmountOfUsedPhotos) {
							message = noAutofillNotification;
						} else if (numberOfEmptyPhotoZones === 0) {
							message = autofillNotificationLabel;
						} else {
							message =
								partialAutofillNotificationLabel &&
								eXpress.utils.formatString(
									partialAutofillNotificationLabel,
									numberOfFilledPhotoZones.toString(),
									numberOfPhotoZones.toString(),
								);
						}
					} else {
						message = autofillNotificationLabel;
					}

					if (!message) return;
					addSnackbar({
						id: "autofill",
						text: message,
					});
				});
		},
		[
			addSnackbar,
			autofillNotificationLabel,
			dashboard,
			editor.definition,
			noAutofillNotification,
			partialAutofillNotificationLabel,
			photosInSelection,
			isDialogOpen,
		],
	);

	const Footer = () => (
		<div className="autofill-dialog__buttons">
			<DialogButton type="button" onClick={() => setIsDialogOpen(false)}>
				{fillOrRefillDialogCancel}
			</DialogButton>
			<DialogButton
				type="button"
				onClick={() => {
					// eslint-disable-next-line @typescript-eslint/no-floating-promises
					doAutofill(eXpress.core.commands.AutoFillType.Fill);
				}}
			>
				{fillOrRefillDialogFill}
			</DialogButton>
			{photosInSelection.some((photo) => photo.useCount > 0) && (
				<DialogButton
					type="button"
					onClick={() => {
						// eslint-disable-next-line @typescript-eslint/no-floating-promises
						doAutofill(eXpress.core.commands.AutoFillType.Refill);
					}}
				>
					{fillOrRefillDialogRefill}
				</DialogButton>
			)}
		</div>
	);

	const handleOnClick = useCallback(() => {
		logGAEvent();
		setIsDialogOpen(true);
	}, [logGAEvent]);

	if (areLabelsLoading) return null;

	return (
		<div>
			<IconButton iconName="magic-staff" onClick={handleOnClick} title={title} />
			<SimpleDialog
				className="autofill-dialog"
				isOpen={isDialogOpen}
				onScrimClick={() => setIsDialogOpen(false)}
				title={fillOrRefillDialogTitle}
				contentHtml={getDialogMessage()}
				footer={<Footer />}
			/>
		</div>
	);
}
